/**
 * feat-300 — fake-mode stubs for every `questionEditService` endpoint.
 *
 * `interviewer-frontend/CLAUDE.md`: "`npm run start:fake` must work with no
 * backend running." The two questions the fake learn-content tree renders
 * (`fake-q-1`, `fake-q-2`, `fakeTenantData.ts`) are seeded with:
 *
 *   fake-q-1 — an open `proposal_ready` thread (a diff where the answer moves),
 *              plus two closed threads (Applied, Discarded) for the history lines;
 *   fake-q-2 — an open `needs_reply` thread (the AI asked a question).
 *
 * Every other state is reachable by INTERACTING, so one page shows them all.
 * A turn is `working` for {@link FAKE_TURN_MS}, then resolves by keyword in the
 * reviewer's latest message (case-insensitive):
 *
 *   "fail"     → failed / ai_error        "refuse"   → failed / ai_refused
 *   "timeout"  → failed / timeout          "invalid"  → failed / ai_invalid_output
 *   "nothing"  → proposal_ready, no_change "stale"    → proposal_ready, is_stale
 *   otherwise  → a clarifying question on the thread's first turn, a proposal after
 *
 * Retry re-runs the last turn and always succeeds. State lives in memory and
 * resets on reload.
 *
 * ⚠️ `config.useDummyData` is `false` in all four environments today
 * (`config/environment.ts`), so these stubs only render once feat-208 flips it —
 * the same caveat `fakeCrewCalloutData.ts` documents.
 */
import type {
  EditableQuestionField,
  InputMode,
  QuestionEditApplyResult,
  QuestionEditMessage,
  QuestionEditProposal,
  QuestionEditThread,
  QuestionEditThreadList,
  TranscribeResult,
} from './questionEditService';
import type { QuizContent, QuizQuestion } from './quizContentAdminService';
import { ApiError } from '../utils/api';
import { fakeGetLearnContent } from './fakeTenantData';

export const FAKE_TURN_MS = 3000;
const FAKE_ME = 'you@bespoke.ai';
const FAKE_OTHER = 'jordan.alvarez@bespoke.ai';

type Outcome =
  | { kind: 'clarify'; question: string }
  | { kind: 'proposal'; proposal: QuestionEditProposal; stale: boolean }
  | { kind: 'failed'; reason: QuestionEditThread['failure_reason'] };

interface FakeThread {
  thread: QuestionEditThread;
  /** When a `working` turn resolves, and to what. */
  pending: { at: number; outcome: Outcome } | null;
}

const iso = (ms: number) => new Date(ms).toISOString();
let seq = 0;
const nextId = (p: string) => `fake-qe-${p}-${(seq += 1)}`;

const store = new Map<string, FakeThread>();

function liveQuestion(id: string): QuizQuestion | null {
  const content = fakeGetLearnContent('fake') as QuizContent;
  for (const u of content.lessons) {
    for (const d of u.drills) {
      const q = d.question_items.find((x) => x.id === id);
      if (q) return q;
    }
  }
  return null;
}

/** Fields the fake AI has already applied, per question, so a second Apply
 *  diffs against the applied text rather than the seed. */
const appliedOverrides = new Map<string, Partial<QuizQuestion>>();

function current(id: string): QuizQuestion | null {
  const q = liveQuestion(id);
  return q ? ({ ...q, ...(appliedOverrides.get(id) ?? {}) } as QuizQuestion) : null;
}

function msg(
  threadSeq: number,
  author: 'reviewer' | 'ai',
  at: number,
  body: { text?: string; mode?: InputMode; email?: string; ai?: QuestionEditMessage['ai'] },
): QuestionEditMessage {
  return {
    id: nextId('m'),
    seq: threadSeq,
    author,
    author_email: author === 'reviewer' ? body.email ?? FAKE_ME : null,
    input_mode: author === 'reviewer' ? body.mode ?? 'typed' : null,
    created_at: iso(at),
    text: author === 'reviewer' ? body.text ?? '' : null,
    ai: author === 'ai' ? body.ai ?? null : null,
  };
}

function baseThread(questionId: string, at: number, email = FAKE_ME): QuestionEditThread {
  return {
    id: nextId('t'),
    question_item_id: questionId,
    state: 'working',
    failure_reason: null,
    created_by_email: email,
    created_at: iso(at),
    updated_at: iso(at),
    working_started_at: iso(at),
    closed_by_email: null,
    closed_at: null,
    clarifies_remaining: 3,
    proposal: null,
    is_stale: false,
    message_count: 0,
    messages: [],
  };
}

function proposalFor(questionId: string): QuestionEditProposal {
  const q = current(questionId);
  if (!q) return { summary: 'No change.', changes: {}, base: {}, no_change: true };
  const letters = ['a', 'b', 'c', 'd'] as const;
  const wrong = letters.find((l) => l !== q.correct_option) ?? 'a';
  const field = `option_${wrong}` as EditableQuestionField;
  const was = q[field as 'option_a'];
  return {
    summary: `Make option ${wrong.toUpperCase()} clearly wrong and tighten the explanation.`,
    changes: {
      [field]: `${was.replace(/[.!?]\s*$/, '')} — and skip the greeting entirely.`,
      explanation: 'Acknowledge every guest within thirty seconds, even when your hands are full.',
    },
    base: { [field]: was, explanation: q.explanation || null },
    no_change: false,
  };
}

function seedThreads(): void {
  const now = Date.now();
  const q1 = current('fake-q-1');
  // fake-q-1: proposal ready, the answer moves (b → a) and option C changes.
  if (q1) {
    const t = baseThread('fake-q-1', now - 40 * 60_000);
    t.messages = [
      msg(1, 'reviewer', now - 40 * 60_000, {
        mode: 'voice',
        text: 'Option C is also kind of right. Make it clearly wrong, and honestly A reads better as the answer.',
      }),
      msg(2, 'ai', now - 39 * 60_000, {
        ai: {
          kind: 'clarify',
          question: 'Should option B stay as a wrong answer, or should I rewrite it too?',
        },
      }),
      msg(3, 'reviewer', now - 12 * 60_000, { mode: 'typed', text: 'Keep B as it is.' }),
      msg(4, 'ai', now - 11 * 60_000, {
        ai: { kind: 'proposal', summary: 'Moves the answer to A and makes C clearly wrong.' },
      }),
    ];
    t.state = 'proposal_ready';
    t.working_started_at = null;
    t.updated_at = iso(now - 11 * 60_000);
    t.clarifies_remaining = 2;
    t.message_count = 4;
    t.proposal = {
      summary: 'Moves the answer to A and makes C clearly wrong.',
      changes: {
        correct_option: 'a',
        option_a: 'Make eye contact and greet them right away, then finish the delivery.',
        option_c: 'Ignore them until all three plates are down.',
      },
      base: { correct_option: q1.correct_option, option_a: q1.option_a, option_c: q1.option_c },
      no_change: false,
    };
    store.set(t.id, { thread: t, pending: null });

    const applied = baseThread('fake-q-1', now - 3 * 86_400_000, FAKE_OTHER);
    applied.state = 'applied';
    applied.working_started_at = null;
    applied.closed_by_email = FAKE_OTHER;
    applied.closed_at = iso(now - 3 * 86_400_000 + 600_000);
    applied.message_count = 2;
    applied.messages = [
      msg(1, 'reviewer', now - 3 * 86_400_000, {
        email: FAKE_OTHER,
        text: 'The praise line is too long.',
      }),
      msg(2, 'ai', now - 3 * 86_400_000 + 60_000, {
        ai: { kind: 'proposal', summary: 'Shortens the right-answer praise.' },
      }),
    ];
    store.set(applied.id, { thread: applied, pending: null });

    const discarded = baseThread('fake-q-1', now - 9 * 86_400_000);
    discarded.state = 'discarded';
    discarded.working_started_at = null;
    discarded.closed_by_email = FAKE_ME;
    discarded.closed_at = iso(now - 9 * 86_400_000 + 300_000);
    discarded.message_count = 1;
    discarded.messages = [
      msg(1, 'reviewer', now - 9 * 86_400_000, { mode: 'voice', text: 'Make D funnier.' }),
    ];
    store.set(discarded.id, { thread: discarded, pending: null });
  }
  // fake-q-2: the AI has a question.
  if (current('fake-q-2')) {
    const t = baseThread('fake-q-2', now - 5 * 60_000, FAKE_OTHER);
    t.messages = [
      msg(1, 'reviewer', now - 5 * 60_000, {
        email: FAKE_OTHER,
        mode: 'voice',
        text: 'Two minutes is too obviously wrong. Make the distractors harder.',
      }),
      msg(2, 'ai', now - 4 * 60_000, {
        ai: {
          kind: 'clarify',
          question:
            'Should the wrong answers stay as times (like “one minute”), or can they be actions too?',
        },
      }),
    ];
    t.state = 'needs_reply';
    t.working_started_at = null;
    t.clarifies_remaining = 2;
    t.message_count = 2;
    store.set(t.id, { thread: t, pending: null });
  }
}

let seeded = false;
function ensureSeeded(): void {
  if (seeded) return;
  seeded = true;
  seedThreads();
}

/** Test seam: drop all fake state (the next call re-seeds). */
export function resetFakeQuestionEdits(): void {
  store.clear();
  appliedOverrides.clear();
  seeded = false;
}

function decide(entry: FakeThread, text: string): Outcome {
  const t = entry.thread;
  const s = text.toLowerCase();
  if (s.includes('refuse')) return { kind: 'failed', reason: 'ai_refused' };
  if (s.includes('timeout')) return { kind: 'failed', reason: 'timeout' };
  if (s.includes('invalid')) return { kind: 'failed', reason: 'ai_invalid_output' };
  if (s.includes('fail')) return { kind: 'failed', reason: 'ai_error' };
  if (s.includes('nothing')) {
    return {
      kind: 'proposal',
      proposal: {
        summary: 'This question already says what you asked for, so nothing needs to change.',
        changes: {},
        base: {},
        no_change: true,
      },
      stale: false,
    };
  }
  const aiTurns = (t.messages ?? []).filter((m) => m.author === 'ai').length;
  if (aiTurns === 0 && t.clarifies_remaining > 0) {
    return {
      kind: 'clarify',
      question: 'Do you want me to change only the wrong answers, or the question wording too?',
    };
  }
  return { kind: 'proposal', proposal: proposalFor(t.question_item_id), stale: s.includes('stale') };
}

function startTurn(entry: FakeThread, text: string): void {
  const now = Date.now();
  const t = entry.thread;
  t.state = 'working';
  t.failure_reason = null;
  t.proposal = null;
  t.is_stale = false;
  t.working_started_at = iso(now);
  t.updated_at = iso(now);
  entry.pending = { at: now + FAKE_TURN_MS, outcome: decide(entry, text) };
}

/** Resolve a turn whose time has come. Reads are what move the fake forward,
 *  which is the same shape as the real server's read-time effective state. */
function settle(entry: FakeThread): void {
  const p = entry.pending;
  if (!p || Date.now() < p.at) return;
  entry.pending = null;
  const t = entry.thread;
  const messages = t.messages ?? [];
  t.working_started_at = null;
  t.updated_at = iso(p.at);
  if (p.outcome.kind === 'failed') {
    t.state = 'failed';
    t.failure_reason = p.outcome.reason;
  } else if (p.outcome.kind === 'clarify') {
    messages.push(msg(messages.length + 1, 'ai', p.at, { ai: { kind: 'clarify', question: p.outcome.question } }));
    t.clarifies_remaining = Math.max(0, t.clarifies_remaining - 1);
    t.state = 'needs_reply';
  } else {
    messages.push(
      msg(messages.length + 1, 'ai', p.at, { ai: { kind: 'proposal', summary: p.outcome.proposal.summary } }),
    );
    t.state = 'proposal_ready';
    t.proposal = p.outcome.proposal;
    t.is_stale = p.outcome.stale;
  }
  t.messages = messages;
  t.message_count = messages.length;
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
const isOpen = (t: QuestionEditThread) =>
  t.state === 'working' || t.state === 'needs_reply' || t.state === 'proposal_ready' || t.state === 'failed';

function conflict(code: string, detail: string, thread?: QuestionEditThread): ApiError {
  return new ApiError(detail, 409, { detail, code, ...(thread ? { thread: clone(thread) } : {}) });
}

function find(threadId: string): FakeThread {
  ensureSeeded();
  const e = store.get(threadId);
  if (!e) throw new ApiError('Not found', 404, { detail: 'Not found' });
  settle(e);
  return e;
}

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ------------------------------------------------------------ endpoints

export async function fakeTranscribe(blob: Blob, signal?: AbortSignal): Promise<TranscribeResult> {
  await delay(900);
  if (signal?.aborted) {
    const err = new Error('Aborted');
    err.name = 'AbortError';
    throw err;
  }
  if (!blob || blob.size === 0) return { text: '', duration_seconds: 0 };
  return {
    text: 'Option C is also technically right. Make it clearly wrong.',
    duration_seconds: 4.2,
  };
}

export async function fakeListThreads(
  tenantId: string,
  scope: 'all' | 'open',
): Promise<QuestionEditThreadList> {
  ensureSeeded();
  const threads: QuestionEditThread[] = [];
  store.forEach((e) => {
    settle(e);
    const open = isOpen(e.thread);
    if (scope === 'open' && !open) return;
    const t = clone(e.thread);
    if (!open) delete t.messages;
    threads.push(t);
  });
  return {
    tenant_id: tenantId,
    approval_required: true,
    server_time: new Date().toISOString(),
    threads,
    closed_truncated: false,
  };
}

export async function fakeGetThread(threadId: string): Promise<QuestionEditThread> {
  await delay(250);
  return clone(find(threadId).thread);
}

export async function fakeOpenThread(
  questionItemId: string,
  text: string,
  inputMode: InputMode,
): Promise<QuestionEditThread> {
  ensureSeeded();
  let existing: QuestionEditThread | null = null;
  store.forEach((e) => {
    settle(e);
    if (e.thread.question_item_id === questionItemId && isOpen(e.thread)) existing = e.thread;
  });
  if (existing) throw conflict('thread_open', 'A thread is already open on this question.', existing);
  const now = Date.now();
  const t = baseThread(questionItemId, now);
  t.messages = [msg(1, 'reviewer', now, { text, mode: inputMode })];
  t.message_count = 1;
  const entry: FakeThread = { thread: t, pending: null };
  store.set(t.id, entry);
  startTurn(entry, text);
  return clone(t);
}

export async function fakeReply(
  threadId: string,
  text: string,
  inputMode: InputMode,
): Promise<QuestionEditThread> {
  const e = find(threadId);
  const t = e.thread;
  if (t.state === 'working') throw conflict('turn_in_progress', 'The AI is still working.', t);
  if (!isOpen(t)) throw conflict('thread_closed', 'This thread is closed.', t);
  const messages = t.messages ?? [];
  messages.push(msg(messages.length + 1, 'reviewer', Date.now(), { text, mode: inputMode }));
  t.messages = messages;
  t.message_count = messages.length;
  startTurn(e, text);
  return clone(t);
}

export async function fakeRetry(threadId: string): Promise<QuestionEditThread> {
  const e = find(threadId);
  const t = e.thread;
  if (!isOpen(t)) throw conflict('thread_closed', 'This thread is closed.', t);
  if (t.state !== 'failed') throw conflict('not_failed', 'Only a failed turn can be retried.', t);
  // A retry always succeeds in the fake, so the failure keyword is not replayed.
  startTurn(e, '');
  return clone(t);
}

export async function fakeApply(threadId: string): Promise<QuestionEditApplyResult> {
  await delay(400);
  const e = find(threadId);
  const t = e.thread;
  if (!isOpen(t)) throw conflict('thread_closed', 'This thread is closed.', t);
  if (t.state !== 'proposal_ready' || !t.proposal) throw conflict('not_ready', 'Nothing to apply.', t);
  if (t.proposal.no_change) throw conflict('no_change', 'Nothing to apply.', t);
  if (t.is_stale) throw conflict('stale_base', 'The question changed.', t);
  const q = current(t.question_item_id);
  if (!q) {
    t.state = 'superseded';
    throw conflict('question_deleted', 'The question was deleted.', t);
  }
  const changes = t.proposal.changes as Partial<QuizQuestion>;
  appliedOverrides.set(t.question_item_id, {
    ...(appliedOverrides.get(t.question_item_id) ?? {}),
    ...changes,
  });
  const now = Date.now();
  t.state = 'applied';
  t.proposal = null;
  t.closed_by_email = FAKE_ME;
  t.closed_at = iso(now);
  t.updated_at = iso(now);
  return {
    thread: clone(t),
    question: { ...q, ...changes } as QuizQuestion,
    approval_required: true,
  };
}

export async function fakeDiscard(threadId: string): Promise<QuestionEditThread> {
  const e = find(threadId);
  const t = e.thread;
  if (t.state === 'discarded') return clone(t);
  if (!isOpen(t)) throw conflict('thread_closed', 'This thread is closed.', t);
  const now = Date.now();
  e.pending = null;
  t.state = 'discarded';
  t.proposal = null;
  t.working_started_at = null;
  t.closed_by_email = FAKE_ME;
  t.closed_at = iso(now);
  t.updated_at = iso(now);
  return clone(t);
}
